import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../auth/useAuth';
import { useTestMode } from '../auth/TestModeContext';
import { usePortalSettings } from '../auth/PortalSettingsContext';
import { api, Notification } from '../services/api';
import { setSessionExpiredHandler } from '../services/api';

// ── Icons ─────────────────────────────────────────────────────
const IconGrid     = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>;
const IconList     = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4h12M2 8h8M2 12h5"/></svg>;
const IconPlus     = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2v12M2 8h12"/></svg>;
const IconCheck    = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8l3 3 7-7"/></svg>;
const IconDoc      = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1.5" y="2" width="13" height="12" rx="1.5"/><path d="M5 2v12M8 6h4M8 9h4"/></svg>;
const IconSettings = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2"/><path d="M8 2v1M8 13v1M2 8h1M13 8h1M3.5 3.5l.7.7M11.8 11.8l.7.7M3.5 12.5l.7-.7M11.8 4.2l.7-.7"/></svg>;
const IconFlask    = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2v5L2.5 13.5a1 1 0 00.9 1.5h9.2a1 1 0 00.9-1.5L10 7V2"/><path d="M5 2h6M6 10h4"/></svg>;
const IconBell     = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2a5 5 0 00-5 5v2l-1 2h12l-1-2V7a5 5 0 00-5-5z"/><path d="M6.5 13a1.5 1.5 0 003 0"/></svg>;
const IconLog      = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M5 6h6M5 9h4"/></svg>;
const IconClock    = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 2"/></svg>;

interface NavItem {
  path:      string;
  label:     string;
  icon:      React.ReactNode;
  section:   string;
  adminOnly?: boolean;
}

const MASQUERADE_ROLES = [
  { value: 'Staff',       label: 'Staff (regular user)' },
  { value: 'Director',    label: 'Director' },
  { value: 'EMT',         label: 'Executive Management Team' },
  { value: 'IT.Approver', label: 'IT Approver' },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, realIsAdmin, isAdmin, isApprover, isDirector, isEMT, isMasquerading, masqueradeRole, setMasquerade, logout, dbUserID } = useAuth();
  const { timeoutWarning } = require('../auth/MasqueradeContext').useMasquerade();
  const { testModeActive, setTestModeActive } = useTestMode();
  const { accounts } = useMsal();
  const { uatVisible, requirementsTrackerVisible, maintenanceModeEnabled } = usePortalSettings();

  const [sidebarOpen, setSidebarOpen]   = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [showBell,      setShowBell]      = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Session expired handler ───────────────────────────────────
  useEffect(() => {
    setSessionExpiredHandler(() => setSessionExpired(true));
  }, []);

  // ── Fire logout event on tab/browser close ────────────────────
  useEffect(() => {
    const handleUnload = () => {
      if (user?.email) {
        // sendBeacon is the correct way to fire requests on page unload
        const body = JSON.stringify({ email: user.email, displayName: user.name || '' });
        navigator.sendBeacon('/api/user/logout', new Blob([body], { type: 'application/json' }));
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user]);

  // ── Notifications polling ─────────────────────────────────────
  const fetchNotifications = useCallback(() => {
    if (!dbUserID) return;
    api.notifications.get(dbUserID)
      .then(res => {
        setNotifications(res.notifications);
        setUnreadCount(res.unreadCount);
      })
      .catch(() => {});
  }, [dbUserID]);

  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, 60_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchNotifications]);

  function markAllRead() {
    if (!dbUserID) return;
    api.notifications.markRead(dbUserID).then(fetchNotifications).catch(() => {});
  }

  // ── Logout with audit event ───────────────────────────────────
  function handleLogout() {
    setShowUserMenu(false);
    if (user?.email) {
      api.users.logout({ email: user.email, displayName: user.name || '' });
    }
    logout();
  }

  // ── Nav items ─────────────────────────────────────────────────
  const navItems: NavItem[] = [
    { path: '/',               label: 'Dashboard',           icon: <IconGrid />,     section: 'Main'      },
    { path: '/projects',       label: 'All Projects',         icon: <IconList />,     section: 'Main'      },
    { path: '/new',            label: 'New Request',          icon: <IconPlus />,     section: 'Main'      },
    ...(uatVisible ? [{ path: '/uat', label: 'UAT Testing', icon: <IconFlask />, section: 'Main' }] : []),
    // Plain Staff never has anything pending -- only show this to roles that actually approve.
    ...(isAdmin || isApprover || isDirector || isEMT ? [{ path: '/approvals', label: 'Pending Review', icon: <IconCheck />, section: 'Approvals' }] : []),
    ...(requirementsTrackerVisible || isAdmin ? [{ path: '/scope', label: 'Requirements Tracker', icon: <IconDoc />, section: 'Projects' }] : []),
    { path: '/admin',          label: 'Admin Settings',       icon: <IconSettings />, section: 'System',   adminOnly: true },
    { path: '/audit',          label: 'Audit Log',            icon: <IconLog />,      section: 'System',   adminOnly: true },
    { path: '/login-history',  label: 'Login History',        icon: <IconClock />,    section: 'System',   adminOnly: true },
  ];

  const sections = ['Main', 'Approvals', 'Projects', 'System'];

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  // ── Breadcrumbs ───────────────────────────────────────────────
  const crumbs = (() => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts.length === 0) return [];
    const labels: Record<string, string> = {
      projects:        'Projects',
      new:             'New Request',
      approvals:       'Approvals',
      scope:           'Requirements',
      admin:           'Admin',
      audit:           'Audit Log',
      'login-history': 'Login History',
    };
    return parts.map((part, i) => ({
      label:  labels[part] || (part.match(/^\d+$/) ? `Project #${part}` : part),
      path:   '/' + parts.slice(0, i + 1).join('/'),
      isLast: i === parts.length - 1,
    }));
  })();

  function handleMasquerade(role: string) { setMasquerade(role as any); setShowUserMenu(false); }
  function exitMasquerade() { setMasquerade(null); setShowUserMenu(false); }

  const masqueradeLabel = MASQUERADE_ROLES.find(r => r.value === masqueradeRole)?.label ?? masqueradeRole;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* ── Banners ───────────────────────────────────────────── */}
      {maintenanceModeEnabled && (
        <div style={{ background: '#92400e', color: '#fef3c7', padding: '10px 20px', fontSize: 13, fontWeight: 500, textAlign: 'center', flexShrink: 0, zIndex: 300 }}>
          🚧 Maintenance mode is active{isAdmin ? ' — non-admin users cannot submit new requests right now.' : ' — new project requests are temporarily disabled. Please check back shortly.'}
        </div>
      )}

      {sessionExpired && (
        <div style={{ background: '#7f1d1d', color: '#fee2e2', padding: '10px 20px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 300 }}>
          <span>⚠️ Your session has expired. Please refresh the page to continue.</span>
          <button onClick={() => window.location.reload()} style={{ background: '#fee2e2', color: '#7f1d1d', border: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>Refresh</button>
        </div>
      )}

      {isMasquerading && timeoutWarning && (
        <div style={{ background: '#92400e', color: '#fef3c7', padding: '8px 20px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 250 }}>
          <span>⏱️ View As session will auto-exit in 5 minutes.</span>
          <button onClick={exitMasquerade} style={{ background: '#fef3c7', color: '#92400e', border: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>Exit Now</button>
        </div>
      )}

      {testModeActive && (
        <div style={{ background: '#065f46', color: '#d1fae5', padding: '8px 20px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 200 }}>
          <span>🧪 <strong>Test Mode active</strong> — all email notifications will be redirected to configured test recipients.</span>
          <button onClick={() => setTestModeActive(false)} style={{ background: '#d1fae5', color: '#065f46', border: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>Exit Test Mode</button>
        </div>
      )}

      {isMasquerading && (
        <div style={{ background: '#92400e', color: '#fef3c7', padding: '8px 20px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 200 }}>
          <span>👁️ Viewing as <strong>{masqueradeLabel}</strong> — UI reflects their permissions. No data is changed.</span>
          <button onClick={exitMasquerade} style={{ background: '#fef3c7', color: '#92400e', border: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>Exit View As</button>
        </div>
      )}

      {/* ── Top bar ───────────────────────────────────────────── */}
      <header style={{ height: 'var(--topbar-height)', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', flexShrink: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setSidebarOpen(s => !s)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }} aria-label="Toggle sidebar">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4h14M2 9h14M2 14h14"/></svg>
          </button>
          <div style={{ width: 28, height: 28, background: 'var(--gold)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
          <div>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>IT Project Portal</div>
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>The Woodlands Township</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Bell */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowBell(s => !s); setShowUserMenu(false); }} style={{ background: showBell ? 'rgba(255,255,255,0.1)' : 'none', border: 'none', cursor: 'pointer', borderRadius: 8, padding: '6px 8px', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', position: 'relative' }} aria-label="Notifications">
              <IconBell />
              {unreadCount > 0 && <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
            {showBell && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', width: 320, zIndex: 500, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text1)' }}>Notifications {unreadCount > 0 && <span style={{ color: 'var(--accent)', fontWeight: 400 }}>({unreadCount} new)</span>}</span>
                  {unreadCount > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent)' }}>Mark all read</button>}
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {notifications.length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No notifications</div>
                  ) : notifications.map(n => (
                    <div key={n.NotificationID}
                      onClick={() => { if (n.LinkUrl) navigate(n.LinkUrl); api.notifications.markRead(dbUserID!, [n.NotificationID]).then(fetchNotifications).catch(() => {}); setShowBell(false); }}
                      style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: n.LinkUrl ? 'pointer' : 'default', background: n.IsRead ? 'var(--surface)' : 'var(--accent-bg, #eff6ff)', transition: 'background 0.1s' }}
                      onMouseEnter={e => { if (n.LinkUrl) (e.currentTarget as HTMLElement).style.background = 'var(--surface2)'; }}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = n.IsRead ? 'var(--surface)' : 'var(--accent-bg, #eff6ff)'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)' }}>{n.Title}</span>
                        {!n.IsRead && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 3 }} />}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4 }}>{n.Message}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{new Date(n.CreatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Avatar */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowUserMenu(s => !s); setShowBell(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, background: showUserMenu ? 'rgba(255,255,255,0.1)' : 'none', border: 'none', cursor: 'pointer', borderRadius: 8, padding: '5px 8px', transition: 'background 0.15s' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>{user?.name || accounts[0]?.name}</div>
                <div style={{ fontSize: 11 }}>
                  {isMasquerading
                    ? <span style={{ color: '#fcd34d' }}>👁️ Viewing as {masqueradeLabel}</span>
                    : <span style={{ color: 'var(--gold-light, #f0c040)' }}>{realIsAdmin ? 'IT Admin' : ''}</span>
                  }
                </div>
              </div>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: isMasquerading ? '#92400e' : testModeActive ? '#065f46' : 'var(--gold)', border: isMasquerading ? '2px solid #fcd34d' : testModeActive ? '2px solid #6ee7b7' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: isMasquerading ? '#fef3c7' : testModeActive ? '#d1fae5' : '#1a1a1a', flexShrink: 0 }}>
                {initials}
              </div>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"><path d={showUserMenu ? 'M2 8l4-4 4 4' : 'M2 4l4 4 4-4'}/></svg>
            </button>

            {showUserMenu && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 240, zIndex: 500, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)' }}>{user?.name || accounts[0]?.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{user?.email}</div>
                  <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4, fontWeight: 500 }}>{realIsAdmin ? 'IT Admin' : 'Staff'}</div>
                </div>
                <div style={{ borderTop: '1px solid var(--border)' }} />
                {realIsAdmin && (
                  <>
                    <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>View portal as...</div>
                    {MASQUERADE_ROLES.map(r => (
                      <button key={r.value} onClick={() => handleMasquerade(r.value)}
                        onMouseEnter={e => { if (masqueradeRole !== r.value) (e.currentTarget as HTMLElement).style.background = 'var(--surface2)'; }}
                        onMouseLeave={e => { if (masqueradeRole !== r.value) (e.currentTarget as HTMLElement).style.background = 'none'; }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '8px 16px', background: masqueradeRole === r.value ? 'var(--accent-bg, #eff6ff)' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: masqueradeRole === r.value ? 'var(--accent)' : 'var(--text1)', fontWeight: masqueradeRole === r.value ? 600 : 400, transition: 'background 0.1s' }}>
                        <span>{r.label}</span>
                        {masqueradeRole === r.value && <span style={{ fontSize: 11 }}>✓</span>}
                      </button>
                    ))}
                    {isMasquerading && (
                      <button onClick={exitMasquerade}
                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--danger-bg)'}
                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'none'}
                        style={{ display: 'block', width: '100%', padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--danger-text, #991b1b)', fontWeight: 500, transition: 'background 0.1s' }}>
                        ✕ Exit View As
                      </button>
                    )}
                    <div style={{ borderTop: '1px solid var(--border)' }} />
                  </>
                )}
                <button onClick={handleLogout}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface2)'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'none'}
                  style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--text1)', fontWeight: 400, transition: 'background 0.1s' }}>
                  → Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <nav style={{ width: sidebarOpen ? 'var(--sidebar-width)' : '0', minWidth: sidebarOpen ? 'var(--sidebar-width)' : '0', background: 'var(--navy-mid)', overflowY: 'auto', overflowX: 'hidden', transition: 'width 0.2s, min-width 0.2s', flexShrink: 0, paddingTop: 12, display: 'flex', flexDirection: 'column' }}>
          {sidebarOpen && sections.map(section => {
            const items = navItems.filter(i => i.section === section);
            if (!items.length) return null;
            const visibleItems = items.filter(i => !i.adminOnly || isAdmin || isApprover);
            if (!visibleItems.length) return null;
            return (
              <div key={section} style={{ marginBottom: 6 }}>
                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '6px 18px 4px' }}>
                  {section}
                </div>
                {visibleItems.map(item => {
                  const active = location.pathname === item.path;
                  return (
                    <button key={item.path} onClick={() => navigate(item.path)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 18px', background: active ? 'rgba(255,255,255,0.1)' : 'none', border: 'none', borderLeft: active ? '2px solid var(--gold)' : '2px solid transparent', color: active ? '#fff' : 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font)', transition: 'all 0.12s' }}
                      onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.85)'; }}
                      onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}
                    >
                      <span style={{ opacity: active ? 1 : 0.7, flexShrink: 0 }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                    </button>
                  );
                })}

                {/* Test Mode toggle — System section only. Visible to real admins and
                    IT.Approver (read-only there too); only real admins can flip it. */}
                {section === 'System' && (realIsAdmin || isApprover) && (
                  <button onClick={() => realIsAdmin && setTestModeActive(!testModeActive)} disabled={!realIsAdmin}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 18px', background: testModeActive ? 'rgba(16,185,129,0.15)' : 'none', border: 'none', borderLeft: testModeActive ? '2px solid #10b981' : '2px solid transparent', color: testModeActive ? '#6ee7b7' : 'rgba(255,255,255,0.6)', fontSize: 13, cursor: realIsAdmin ? 'pointer' : 'default', textAlign: 'left', fontFamily: 'var(--font)', transition: 'all 0.12s' }}
                    onMouseEnter={e => { if (!testModeActive && realIsAdmin) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.85)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = testModeActive ? '#6ee7b7' : 'rgba(255,255,255,0.6)'; }}
                  >
                    <span style={{ opacity: testModeActive ? 1 : 0.7, flexShrink: 0 }}><IconFlask /></span>
                    <span style={{ flex: 1 }}>Test Mode</span>
                    {testModeActive && <span style={{ background: '#10b981', color: '#fff', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8 }}>ON</span>}
                  </button>
                )}
              </div>
            );
          })}
        </nav>

        <main style={{ flex: 1, overflowY: 'auto', padding: '24px', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
          {crumbs.length > 0 && (
            <nav aria-label="breadcrumb" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)' }}>
              <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => navigate('/')}>Home</span>
              {crumbs.map(crumb => (
                <React.Fragment key={crumb.path}>
                  <span>›</span>
                  {crumb.isLast
                    ? <span style={{ color: 'var(--text2)', fontWeight: 500 }}>{crumb.label}</span>
                    : <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => navigate(crumb.path)}>{crumb.label}</span>
                  }
                </React.Fragment>
              ))}
            </nav>
          )}
          <div style={{ flex: 1 }}>{children}</div>
        </main>
      </div>

      {(showUserMenu || showBell) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1 }} onClick={() => { setShowUserMenu(false); setShowBell(false); }} />
      )}
    </div>
  );
}
